"use client";

import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import hooks from "@/hooks";
import { Form } from "@/components/ui/form";
import FormFields from "../Fields";
import Buttons from "@/components/core/Buttons";
import {
  CreateAiConfigurationData,
  CreateAiConfigurationFormSchema,
} from "@/components/shared/Forms/CreateAiConfigurationForm/schema";
import { MethodTypeEnum, ModuleTypeEnum } from "@/enum";
import { v4 as uuidv4 } from "uuid";
import { ITool } from "@/actions/ai-configuration/tool/types";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { List, MoreVertical, Pencil, Plus, X } from "lucide-react";
import EditorToolModal from "@/components/layouts/ToolModals/EditorToolModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DetailToolModal from "@/components/layouts/ToolModals/DetailToolModal";
import { useTranslations } from "next-intl";

interface ICreateAiConfigurationFormProps {
  onSuccess?: () => void;
}

const CreateAiConfigurationForm = ({
  onSuccess,
}: ICreateAiConfigurationFormProps) => {
  const { toast } = useToast();

  const form = useForm<CreateAiConfigurationData>({
    resolver: zodResolver(CreateAiConfigurationFormSchema),
    defaultValues: {
      aiConfigurationId: uuidv4(),
      apiUrl: "",
      description: "",
      type: ModuleTypeEnum.CUSTOMER,
      tools: [],
    },
  });

  const t = useTranslations("AiConfiguration");

  const toolModel = {
    id: uuidv4(),
    name: "",
    apiUrl: "",
    description: "",
    methodType: MethodTypeEnum.GET,
    type: ModuleTypeEnum.CUSTOMER,
  } as ITool;

  const { fields, append, update, remove } = useFieldArray({
    control: form.control,
    name: "tools",
  });

  const createConfigurationService = hooks.useCreateAiConfiguration({
    onSuccess: () => {
      toast({
        title: "Configuração criada com sucesso!",
        variant: "success",
      });
      if (onSuccess) onSuccess();
    },
    onError: (error: unknown) => {
      toast({
        title: "Não foi possível criar configuração",
        description: (error as Error).message,
        variant: "warning",
      });
    },
  });

  async function onSubmit(data: CreateAiConfigurationData) {
    await createConfigurationService.mutateAsync(data);
  }

  const [isDetailToolModalOpen, setDetailToolModalOpen] = useState(false);
  const [isEditorToolModalOpen, setEditorToolModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [viewTool, setViewTool] = useState<ITool | null>(null);
  const [editingTool, setEditingTool] = useState<ITool | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [initialValues, setInitialValues] = useState<CreateAiConfigurationData>(
    form.getValues(),
  );

  const openCreateToolModal = () => {
    setInitialValues(structuredClone(form.getValues()));

    const newIndex = fields.length;
    append(toolModel);

    setIsEditing(false);
    setEditingIndex(newIndex);
    setEditingTool(toolModel);
    setEditorToolModalOpen(true);
  };

  const openEditToolModal = (tool: ITool, index: number) => {
    setInitialValues(structuredClone(form.getValues()));
    setEditingIndex(index);
    setEditingTool(tool);
    setIsEditing(true);
    setEditorToolModalOpen(true);
  };

  const openDetailToolModal = (tool: ITool) => {
    setDetailToolModalOpen(true);
    setViewTool(tool);
  };

  const handleSaveTool = (tool: ITool, index: number) => {
    update(index, tool);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full flex flex-col items-center gap-y-6"
      >
        <FormFields.InputFormField
          form={form}
          formName="apiUrl"
          placeholder="Insira a URL da configuração"
          label="URL"
        />

        <FormFields.SelectFormField
          form={form}
          formName="type"
          label="Tipo"
          placeholder="Selecione o tipo"
          options={[{ label: "Agente", value: "AGENT" }]}
        />

        <FormFields.TextareaFormField
          form={form}
          formName="description"
          label="Descrição (Prompt)"
          placeholder="Insira a descrição da configuração"
          rows={6}
        />

        <Button className="w-full" type="button" onClick={openCreateToolModal}>
          <Plus className={`h-5 w-5 mr-1`} />
          Adicionar Ferramenta
        </Button>

        {fields.length > 0 ? (
          <div className="w-full overflow-x-auto">
            <table className="w-full md:min-w-[600px]">
              <thead>
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer select-none">
                    <div className="flex items-center gap-1">
                      <div className="max-w-xs">Nome</div>
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer select-none">
                    <div className="flex items-center gap-1">
                      <div className="max-w-xs">Método</div>
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer select-none">
                    <div className="flex items-center gap-1">
                      <div className="max-w-xs">Tipo</div>
                    </div>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer select-none">
                    <div className="flex items-center gap-1">
                      <div className="text-center w-full">Ações</div>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {fields.map((field, index) => (
                  <tr
                    key={field.id}
                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4">{field.name}</td>

                    <td className="py-3 px-4">{field.methodType}</td>

                    <td className="py-3 px-4">{t(`types.${field.type}`)}</td>

                    <td className="flex items-center justify-center w-full">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditToolModal(field, index)}
                        >
                          <Pencil></Pencil>
                        </Button>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>

                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => openDetailToolModal(field)}
                              className="flex items-center gap-2 cursor-pointer"
                            >
                              <List className="h-4 w-4" />
                              Detalhes
                            </DropdownMenuItem>

                            <DropdownMenuItem
                              className="flex items-center gap-2 cursor-pointer text-red-600 focus:text-red-600"
                              onClick={() => remove(index)}
                            >
                              <X className="h-4 w-4" />
                              Remover
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <tr>
            <td colSpan={4} className="py-4 text-center text-muted-foreground">
              Nenhuma Ferramenta encontrada.
            </td>
          </tr>
        )}

        <Buttons.DefaultButton
          className="py-6"
          type="submit"
          title="Salvar configuração"
          isLoading={createConfigurationService.isPending}
        />
      </form>

      {isEditorToolModalOpen && (
        <EditorToolModal
          isOpen={isEditorToolModalOpen}
          onOpenChange={setEditorToolModalOpen}
          onSave={handleSaveTool}
          index={editingIndex}
          form={form}
          initialValues={initialValues}
          isEditing={isEditing}
          {...editingTool}
        />
      )}

      {isDetailToolModalOpen && (
        <DetailToolModal
          isOpen={isDetailToolModalOpen}
          onOpenChange={setDetailToolModalOpen}
          isError={!viewTool}
          toolData={viewTool}
        />
      )}
    </Form>
  );
};

export default CreateAiConfigurationForm;
